import { BenefitTypeDisplayTextMap, BenefitTypes } from '@strata/constants';
import { conditionalString, formatDate, sortAsc } from '@strata/helper-functions';
import { Grid, SecondaryData } from '@strata/react-grid';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import { PayerCapabilityForm } from 'pages/npiManagement/payerCapabilities/PayerCapabilityForm';
import { isCapabilityActive } from 'services/CapabilityHelpers';
import { ContractTypeOptions, CREATE_ROUTE } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { get, useGet } from 'services/Http';
import { getPayerSegmentMap, mapPayerSegment } from 'services/PayerSegmentSupport';

const columns = [
    {
        header: 'Payer Segment',
        accessorKey: 'segment_name',
        size: 150,
    },
    {
        header: 'Benefit',
        accessorKey: 'benefit',
        size: 100,
        cell: ({ getValue }) => <SecondaryData data={BenefitTypeDisplayTextMap[getValue()]} />,
    },
    {
        header: 'Line Of Business',
        accessorKey: 'line_of_business',
        size: 100,
    },
    {
        header: 'Market',
        accessorKey: 'lob_segment',
        size: 100,
    },
    {
        header: 'Contract Type',
        accessorKey: 'contract_type',
        size: 150,
        cell: ({ getValue }) => (
            <SecondaryData
                data={ContractTypeOptions.find(({ value }) => value === getValue())?.name}
            />
        ),
    },
    {
        header: 'Live Referral Events',
        accessorKey: 'no_market',
        size: 110,
        cell: ({ getValue }) => <SecondaryData data={getValue() ? 'Yes' : 'No'} />,
    },
    {
        header: 'Start Date',
        accessorKey: 'start_date',
        size: 100,
        cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
    },
    {
        header: 'End Date',
        accessorKey: 'end_date',
        size: 100,
        cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
    },
];

export const PayerCapabilityDetails = () => {
    const { npi, payerId } = useParams();

    const isCreate = payerId === CREATE_ROUTE;

    const [payerCapabilities, setPayerCapabilities] = useState();
    useEffect(() => {
        void (async () => {
            try {
                setPayerCapabilities(
                    (await get(`/reference/capabilities/${npi}`))?.payer_capabilities?.filter(
                        (cap) => cap.payer_id === payerId,
                    ),
                );
            } catch (err) {
                handleError('Error fetching payer capabilities', err);
            }
        })();
    }, [npi, payerId]);

    const payers = useGet('/reference/payers/');
    const payerOptions = useMemo(
        () => payers?.map((payer) => ({ name: payer.payer_name, secondaryData: payer.payer_id })),
        [payers],
    );
    const payerSegments = useGet('/reference/payer_segments');
    const payerSegmentMap = getPayerSegmentMap(payerSegments);
    const payerSegmentOptions = useMemo(() => payerSegments?.map(mapPayerSegment), [payerSegments]);

    const isLoading = !(payerCapabilities && payers && payerSegments);

    const { activeCapabilities, pastCapabilities } = useMemo(() => {
        const capabilities = {
            activeCapabilities: Object.values(BenefitTypes).reduce(
                (prev, curr) => ({ ...prev, [curr]: [] }),
                {},
            ),
            pastCapabilities: [],
        };

        payerCapabilities?.forEach((cap) => {
            if (isCapabilityActive(cap)) {
                capabilities.activeCapabilities[cap.benefit]?.push(cap);
            } else {
                capabilities.pastCapabilities.push({
                    ...cap,
                    segment_name: payerSegmentMap?.[cap.segment_id]?.segment_name,
                });
            }
        });

        Object.values(capabilities.activeCapabilities).forEach((caps) => {
            caps.sort(
                (a, b) =>
                    sortAsc(
                        payerSegmentMap?.[a.segment_id]?.segment_name,
                        payerSegmentMap?.[b.segment_id]?.segment_name,
                    ) ||
                    sortAsc(a.lob_segment, b.lob_segment) ||
                    sortAsc(a.line_of_business, b.line_of_business) ||
                    sortAsc(a.contract_type, b.contract_type),
            );
        });

        return capabilities;
    }, [payerCapabilities, payerSegmentMap]);

    const formTitle = useMemo(() => {
        if (isCreate) {
            return 'Add Payer Capability';
        }
        const payerName = payerOptions?.find(
            ({ secondaryData }) => secondaryData === payerId,
        )?.name;
        const payerNameStr = conditionalString(!!payerName, `${payerName} - `);
        return `${payerNameStr}${payerId} Capabilities`;
    }, [isCreate, payerId, payerOptions]);

    return (
        <div className="sds_payer-capabilities__form">
            <PayerCapabilityForm
                formTitle={formTitle}
                isLoading={isLoading}
                activeCapabilities={activeCapabilities}
                payerOptions={payerOptions}
                payerSegmentOptions={payerSegmentOptions}
            />
            {pastCapabilities.length > 0 && (
                <>
                    <div className="font-bold--uppercase">Past Capabilities</div>
                    <Grid
                        data={pastCapabilities}
                        isLoading={isLoading}
                        sortProperties={[{ id: 'end_date', desc: true }]}
                        columns={columns}
                    />
                </>
            )}
        </div>
    );
};
